import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { Button } from '@/client/components/ui/button';
import { Card } from '@/client/components/ui/card';
import { cn } from '@/client/lib/utils';
import type { RouterOutputs } from '@/client/utils/trpc';
import { ChevronLeft, ChevronRight, UserX } from 'lucide-react';
import { TYPE_META } from './constants';

type TimeOffRequest = RouterOutputs['timeoff']['list'][number];

interface TimeOffCalendarProps {
  requests: TimeOffRequest[];
  monthOffset: number;
  onMonthChange: (offset: number) => void;
  tab: 'team' | 'me';
  currentUserId: string;
}

function getMonthInfo(offset: number) {
  const base = new Date();
  const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const daysInMonth = new Date(year, month, 0).getDate();
  let firstDow = d.getDay() - 1; // Mon=0 … Sun=6
  if (firstDow < 0) firstDow = 6;
  const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  return { year, month, daysInMonth, firstDow, label };
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toDateStr(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export function TimeOffCalendar({
  requests,
  monthOffset,
  onMonthChange,
  tab,
  currentUserId,
}: TimeOffCalendarProps) {
  const { year, month, daysInMonth, firstDow, label } = getMonthInfo(monthOffset);
  const today = todayStr();
  const monthStart = toDateStr(year, month, 1);

  // Build grid cells: nulls for padding, then 1…daysInMonth
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const visible = requests.filter((r) => r.status !== 'rejected');
  const calendarBars = tab === 'me' ? visible.filter((r) => r.user_id === currentUserId) : visible;
  const outToday = visible.filter((r) => r.date_from <= today && r.date_to >= today);

  function dayBars(day: number): TimeOffRequest[] {
    const ds = toDateStr(year, month, day);
    return calendarBars.filter((r) => r.date_from <= ds && r.date_to >= ds);
  }

  function isWeekend(day: number): boolean {
    const dow = new Date(year, month - 1, day).getDay();
    return dow === 0 || dow === 6;
  }

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-7">
      {/* Month nav + legend */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => onMonthChange(monthOffset - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[160px] text-center text-lg font-semibold">{label}</span>
          <Button variant="ghost" size="icon" onClick={() => onMonthChange(monthOffset + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={() => onMonthChange(0)}>
          Today
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-4">
          {Object.entries(TYPE_META).map(([key, meta]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: meta.color }}
              />
              <span className="text-xs text-muted-foreground">{meta.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Out today strip */}
      <Card className="p-0">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <UserX className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">Out today</div>
              <div className="text-xs text-muted-foreground">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: '2-digit',
                  day: '2-digit',
                })}
              </div>
            </div>
          </div>
          <div className="h-8 w-px bg-border" />
          {outToday.length === 0 ? (
            <span className="text-sm text-muted-foreground">Everyone is in today</span>
          ) : (
            <div className="flex flex-1 flex-wrap items-center gap-4">
              {outToday.map((r) => {
                const meta = TYPE_META[r.type];
                const name = r.user.full_name ?? 'Unknown';
                return (
                  <div key={r.id} className="flex items-center gap-2">
                    <InitialsAvatar name={name} size={26} />
                    <div>
                      <div className="text-sm font-medium">{name}</div>
                      <div
                        className="flex items-center gap-1 text-xs"
                        style={{ color: meta?.color }}
                      >
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-sm"
                          style={{ background: meta?.color }}
                        />
                        {meta?.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Calendar grid */}
      <Card className="shrink-0 overflow-hidden p-0">
        <div className="grid grid-cols-7 border-b">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
            <div
              key={d}
              className={cn(
                'px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
                i > 0 && 'border-l',
              )}
            >
              {d}
            </div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div
            key={wi}
            className={cn('grid min-h-[104px] grid-cols-7', wi < weeks.length - 1 && 'border-b')}
          >
            {week.map((day, di) => {
              const bars = day ? dayBars(day) : [];
              const ds = day ? toDateStr(year, month, day) : '';
              const isToday = ds === today;
              const weekend = day ? isWeekend(day) : false;

              return (
                <div
                  key={di}
                  className={cn(
                    'flex flex-col gap-0.5 p-1.5',
                    di > 0 && 'border-l',
                    !day && 'bg-muted/60',
                    weekend && day && 'bg-muted/30',
                  )}
                >
                  {day && (
                    <div className="flex justify-end px-1 py-0.5">
                      <span
                        className={cn(
                          'inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium',
                          isToday && 'bg-signal font-bold text-signal-foreground',
                          !isToday && weekend && 'text-muted-foreground',
                        )}
                      >
                        {day}
                      </span>
                    </div>
                  )}
                  {bars.slice(0, 3).map((r) => {
                    const meta = TYPE_META[r.type];
                    const name = r.user.full_name ?? '';
                    // Show name on the first visible cell of this bar in the month
                    const isStart = r.date_from === ds || (day === 1 && r.date_from < monthStart);
                    return (
                      <div
                        key={r.id}
                        title={`${name} · ${meta?.label ?? r.type}${r.status === 'pending' ? ' (pending)' : ''}`}
                        className="flex h-5 items-center gap-1 overflow-hidden whitespace-nowrap rounded px-1.5 text-[11px] font-medium text-white"
                        style={{
                          background: meta?.color ?? '#888',
                          opacity: r.status === 'pending' ? 0.55 : 1,
                          border:
                            r.status === 'pending' ? '1px dashed rgba(255,255,255,0.6)' : 'none',
                        }}
                      >
                        {isStart && (
                          <>
                            <InitialsAvatar name={name} size={14} />
                            <span className="overflow-hidden text-ellipsis">
                              {name.split(' ')[0]}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {bars.length > 3 && (
                    <span className="pl-1 text-[10px] text-muted-foreground">
                      +{bars.length - 3} more
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </Card>

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span
          className="inline-block h-2.5 w-3.5 rounded-sm opacity-50"
          style={{ background: '#2563eb', border: '1px dashed rgba(100,100,255,0.6)' }}
        />
        Dashed / faded bars are pending approval and don't yet count as confirmed absence.
      </p>
    </div>
  );
}
